import Alpine from "alpinejs";
import EmblaCarousel from "embla-carousel";
import Fade from "embla-carousel-fade";

/**
 * Initialize Embla carousel
 */
export function initCarousel() {
	document.addEventListener("alpine:init", () => {
		Alpine.data("carousel", (count) => ({
			// Data
			emblaApi: null,
			carouselId: null,
			selectedIndex: 0,
			slideCount: count,
			canScrollPrev: false,
			canScrollNext: false,

			// Initialization
			init() {
				this.carouselId = this.$id("carousel");

				// Parse Embla options
				const optionsAttr = this.$el.getAttribute("data-embla-options");
				const options = optionsAttr ? JSON.parse(optionsAttr) : {};
				const effect = this.$el.getAttribute("data-embla-effect") || "slide";
				const plugins = effect === "fade" ? [Fade()] : [];

				// Initialize Embla with optional effect plugin
				this.emblaApi = EmblaCarousel(this.$refs.viewport, options, plugins);

				// Set up event listeners
				this.emblaApi.on("select", () => this.updateState());
				this.emblaApi.on("init", () => this.updateState());

				// Initial state
				this.updateState();

				// Re-measure after the initial layout and when image dimensions settle.
				const reInit = () => this.emblaApi?.reInit();
				this.$el.querySelectorAll("img").forEach((image) => {
					if (!image.complete) {
						image.addEventListener("load", reInit, { once: true });
					}
				});
				requestAnimationFrame(reInit);
			},

			// State management
			updateState() {
				if (!this.emblaApi) return;

				this.selectedIndex = this.emblaApi.selectedScrollSnap();
				this.canScrollPrev = this.emblaApi.canScrollPrev();
				this.canScrollNext = this.emblaApi.canScrollNext();
			},

			// Navigation methods
			scrollPrev() {
				this.emblaApi.scrollPrev();
			},

			scrollNext() {
				this.emblaApi.scrollNext();
			},

			scrollTo(index) {
				this.emblaApi.scrollTo(index);
			},

			// Keyboard navigation
			handleKeydown(event) {
				const handlers = {
					ArrowLeft: () => this.focusPrevTab(),
					ArrowUp: () => this.focusPrevTab(),
					ArrowRight: () => this.focusNextTab(),
					ArrowDown: () => this.focusNextTab(),
					Home: () => this.focusTab(0),
					End: () => this.focusTab(this.slideCount - 1),
				};

				const handler = handlers[event.key];
				if (handler) {
					event.preventDefault();
					handler();
				}
			},

			focusPrevTab() {
				const isLooping = this.emblaApi.internalEngine().options.loop;

				if (this.selectedIndex === 0) {
					if (isLooping) {
						this.focusTab(this.slideCount - 1);
					}
				} else {
					this.focusTab(this.selectedIndex - 1);
				}
			},

			focusNextTab() {
				const isLooping = this.emblaApi.internalEngine().options.loop;
				const isLastSlide = this.selectedIndex === this.slideCount - 1;

				if (isLastSlide) {
					if (isLooping) {
						this.focusTab(0);
					}
				} else {
					this.focusTab(this.selectedIndex + 1);
				}
			},

			focusTab(index) {
				this.scrollTo(index);
				this.$nextTick(() => {
					const tab = this.$refs.tablist.querySelector(
						`[role="tab"]:nth-child(${index + 1})`,
					);
					tab?.focus();
				});
			},
		}));
	});
}
